function setHeaderHeights(root) {
  const header = root.closest('#header-component') || root;
  const group = document.querySelector('#header-group');
  // Exclude the open mobile drawer so the header height stays stable while it's open.
  const drawer = root.querySelector('[data-nav-drawer]');
  const drawerHeight = drawer && !drawer.hidden ? drawer.offsetHeight : 0;
  const headerHeight = Math.max(0, (header.offsetHeight || 0) - drawerHeight);
  let groupHeight = 0;
  if (group) {
    for (const el of group.children) {
      if (el instanceof HTMLElement) groupHeight += el.offsetHeight;
    }
    groupHeight = Math.max(0, groupHeight - drawerHeight);
  }
  document.body.style.setProperty('--header-height', `${headerHeight}px`);
  document.body.style.setProperty('--header-group-height', `${groupHeight || headerHeight}px`);
}

function prefersReduce() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatCartMoney(cents, format) {
  const amount = Number(cents) || 0;
  if (typeof window.Shopify?.formatMoney === 'function') {
    return window.Shopify.formatMoney(amount, format);
  }
  const value = (amount / 100).toFixed(2);
  const withCommas = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const noDecimals = String(Math.round(amount / 100));
  const tpl = format || '{{amount}}';
  return tpl
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommas)
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
    .replace(/\{\{\s*amount\s*\}\}/g, withCommas);
}

function itemImageUrl(item) {
  const raw = item?.image || item?.featured_image?.url || item?.featured_image || '';
  if (!raw) return '';
  if (raw.includes('_{width}x')) return raw.replace('_{width}x', '_120x');
  if (raw.includes('?')) return `${raw}&width=120`;
  return `${raw}?width=120`;
}

function setBagCounts(count) {
  const qty = Math.max(0, Number(count) || 0);
  document.querySelectorAll('[data-bag-count]').forEach((el) => {
    const next = String(qty);
    if (el.textContent === next) return;
    el.textContent = next;
    el.classList.remove('is-bump');
    void el.offsetWidth;
    el.classList.add('is-bump');
  });
  return qty;
}

function renderCartDropdown(cart) {
  const drops = document.querySelectorAll('[data-artink-header] [data-cart-dropdown]');
  drops.forEach((drop) => {
    const body = drop.querySelector('[data-cart-body]');
    if (!body) return;

    const emptyText = drop.dataset.emptyText || 'Your cart is empty.';
    const subtotalLabel = drop.dataset.subtotalLabel || 'Subtotal';
    const viewCartLabel = drop.dataset.viewCartLabel || 'View cart';
    const checkoutLabel = drop.dataset.checkoutLabel || 'Checkout';
    const cartUrl = drop.dataset.cartUrl || '/cart';
    const checkoutUrl = drop.dataset.checkoutUrl || '/checkout';
    const moneyFormat = drop.dataset.moneyFormat || '{{amount}}';
    const removeLabel = drop.dataset.removeLabel || 'Remove';
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const count = Number(cart?.item_count) || 0;

    if (!count || !items.length) {
      body.innerHTML = `<p class="artink-bar__cart-empty">${escapeHtml(emptyText)}</p>`;
      return;
    }

    const list = items
      .slice(0, 6)
      .map((item) => {
        const title = escapeHtml(item.product_title || item.title || 'Product');
        const url = escapeHtml(item.url || '#');
        const qty = Number(item.quantity) || 1;
        const line = formatCartMoney(item.final_line_price ?? item.line_price ?? 0, moneyFormat);
        const img = itemImageUrl(item);
        const imgHtml = img
          ? `<img src="${escapeHtml(img)}" alt="${title}" width="52" height="52" loading="lazy">`
          : '';
        return `<li class="artink-bar__cart-item">
          <a class="artink-bar__cart-img" href="${url}">${imgHtml}</a>
          <div class="artink-bar__cart-meta">
            <a href="${url}">${title}</a>
            <p>${qty} × ${escapeHtml(line)}</p>
          </div>
          <button type="button" class="artink-bar__cart-remove" data-cart-remove="${escapeHtml(item.key)}" aria-label="${escapeHtml(removeLabel)} ${title}">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
          </button>
        </li>`;
      })
      .join('');

    const subtotal = formatCartMoney(cart.total_price ?? 0, moneyFormat);
    body.innerHTML = `
      <ul class="artink-bar__cart-list">${list}</ul>
      <div class="artink-bar__cart-foot">
        <div class="artink-bar__cart-sub">
          <span>${escapeHtml(subtotalLabel)}</span>
          <strong data-cart-subtotal>${escapeHtml(subtotal)}</strong>
        </div>
        <a class="artink-bar__cart-btn artink-bar__cart-btn--ghost" href="${escapeHtml(cartUrl)}">${escapeHtml(viewCartLabel)}</a>
        <a class="artink-bar__cart-btn" href="${escapeHtml(checkoutUrl)}">${escapeHtml(checkoutLabel)}</a>
      </div>
    `;
  });
}

async function refreshCartCount() {
  const base = window.Theme?.routes?.cart_url || '/cart';
  const url = base.endsWith('.js') ? base : `${base}.js`;
  const res = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!res.ok) throw new Error('Could not refresh cart');
  const cart = await res.json();
  const qty = setBagCounts(cart.item_count);
  renderCartDropdown(cart);
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      detail: { cart, itemCount: qty },
    })
  );
  return cart;
}

async function openArtinkCartDropdown() {
  const toggle = document.querySelector('[data-artink-header] [data-cart-toggle]');
  const drop = document.querySelector('[data-artink-header] [data-cart-dropdown]');
  if (!toggle || !drop) return false;
  try {
    await refreshCartCount();
  } catch {
    /* still open with last known markup */
  }
  drop.hidden = false;
  toggle.setAttribute('aria-expanded', 'true');
  return true;
}

window.ArtinkCart = {
  setCount: setBagCounts,
  refresh: refreshCartCount,
  render: renderCartDropdown,
  openDropdown: openArtinkCartDropdown,
};

function patchOpenCart() {
  try {
    const actions = window.Shopify?.actions;
    if (!actions?.openCart || actions.__artinkCartPatched) return;
    actions.openCart = async () => {
      await openArtinkCartDropdown();
    };
    actions.__artinkCartPatched = true;
  } catch {
    /* ignore */
  }
}

patchOpenCart();
document.addEventListener('DOMContentLoaded', patchOpenCart, { once: true });

function bootHeader(root) {
  if (!root || root.dataset.booted === '1') return;
  root.dataset.booted = '1';

  const bar = root.querySelector('.artink-bar') || root;
  const drops = [...root.querySelectorAll('[data-drop]')];
  const megaTriggers = [...root.querySelectorAll('[data-mega]')];
  const megaPanels = [...root.querySelectorAll('[data-panel]')];
  const cartWrap = root.querySelector('[data-cart-wrap]');
  const cartToggle = root.querySelector('[data-cart-toggle]');
  const cartDrop = root.querySelector('[data-cart-dropdown]');
  const burger = root.querySelector('[data-nav-burger]');
  const drawer = root.querySelector('[data-nav-drawer]');
  const bagCounts = [...root.querySelectorAll('[data-bag-count]')];
  const announceItems = [...root.querySelectorAll('[data-announce-item]')];
  let announceIndex = 0;
  let announceTimer = 0;
  let megaTimer = 0;

  function closeDrops(except) {
    drops.forEach((drop) => {
      if (except && drop === except) return;
      const trigger = drop.querySelector('[data-drop-trigger]');
      const panel = drop.querySelector('[data-drop-panel]');
      trigger?.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
    });
  }

  function closeMega() {
    megaTriggers.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    megaPanels.forEach((panel) => {
      panel.hidden = true;
    });
    bar.classList.remove('is-mega');
  }

  function openMega(name) {
    if (!name) return;
    closeDrops();
    closeCart();
    let opened = false;
    megaTriggers.forEach((btn) => {
      const on = btn.dataset.mega === name;
      btn.setAttribute('aria-expanded', on ? 'true' : 'false');
      if (on) opened = true;
    });
    megaPanels.forEach((panel) => {
      panel.hidden = panel.dataset.panel !== name;
    });
    bar.classList.toggle('is-mega', opened);
  }

  function closeCart() {
    if (!cartDrop || !cartToggle) return;
    cartDrop.hidden = true;
    cartToggle.setAttribute('aria-expanded', 'false');
  }

  function openCart() {
    if (!cartDrop || !cartToggle) return;
    closeDrops();
    closeMega();
    cartDrop.hidden = false;
    cartToggle.setAttribute('aria-expanded', 'true');
  }

  async function toggleCart(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!cartDrop) return;
    if (cartDrop.hidden) {
      try {
        await refreshCartCount();
      } catch {
        /* keep existing dropdown markup */
      }
      openCart();
    } else {
      closeCart();
    }
  }

  drops.forEach((drop) => {
    const trigger = drop.querySelector('[data-drop-trigger]');
    const panel = drop.querySelector('[data-drop-panel]');
    if (!trigger || !panel) return;

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const open = trigger.getAttribute('aria-expanded') === 'true';
      closeDrops(drop);
      closeMega();
      closeCart();
      trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
      panel.hidden = open;
    });

    drop.addEventListener('pointerenter', () => {
      if (window.matchMedia('(max-width: 1100px)').matches) return;
      closeDrops(drop);
      closeMega();
      closeCart();
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
    });

    drop.addEventListener('pointerleave', () => {
      if (window.matchMedia('(max-width: 1100px)').matches) return;
      trigger.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
    });
  });

  megaTriggers.forEach((btn) => {
    btn.addEventListener('pointerenter', () => {
      if (window.matchMedia('(max-width: 1100px)').matches) return;
      window.clearTimeout(megaTimer);
      openMega(btn.dataset.mega);
    });
  });

  // Keyboard: focusing the trigger opens the panel; ArrowDown moves into it.
  const inMega = (node) =>
    node instanceof Element && !!(node.closest('[data-panel]') || node.closest('[data-mega-wrap]'));
  megaTriggers.forEach((btn) => {
    btn.addEventListener('focus', () => openMega(btn.dataset.mega));
    btn.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown') return;
      const panel = megaPanels.find((p) => p.dataset.panel === btn.dataset.mega);
      const first = panel?.querySelector('a, button');
      if (!first) return;
      event.preventDefault();
      openMega(btn.dataset.mega);
      first.focus();
    });
    btn.addEventListener('focusout', (event) => {
      if (!inMega(event.relatedTarget)) closeMega();
    });
  });

  megaPanels.forEach((panel) => {
    panel.addEventListener('focusout', (event) => {
      if (!inMega(event.relatedTarget)) closeMega();
    });
    panel.addEventListener('pointerenter', () => window.clearTimeout(megaTimer));
    panel.addEventListener('pointerleave', () => {
      window.clearTimeout(megaTimer);
      megaTimer = window.setTimeout(closeMega, prefersReduce() ? 0 : 160);
    });
  });

  root.querySelectorAll('[data-mega-wrap]').forEach((wrap) => {
    wrap.addEventListener('pointerleave', () => {
      if (window.matchMedia('(max-width: 1100px)').matches) return;
      window.clearTimeout(megaTimer);
      megaTimer = window.setTimeout(closeMega, prefersReduce() ? 0 : 160);
    });
  });

  cartToggle?.addEventListener('click', toggleCart);

  const onCartRemove = async (event) => {
    const btn = event.target.closest('[data-cart-remove]');
    if (!btn || btn.getAttribute('aria-busy') === 'true') return;
    // Keep the dropdown open: the button is detached by the re-render below.
    event.stopPropagation();
    btn.setAttribute('aria-busy', 'true');
    try {
      const base = window.Theme?.routes?.cart_change_url || '/cart/change';
      const url = base.endsWith('.js') ? base : `${base}.js`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: btn.dataset.cartRemove, quantity: 0 }),
      });
      if (!res.ok) throw new Error('Could not remove item');
      const cart = await res.json();
      const qty = setBagCounts(cart.item_count);
      renderCartDropdown(cart);
      document.dispatchEvent(new CustomEvent('cart:update', { detail: { cart, itemCount: qty } }));
      const next = cartDrop?.querySelector('[data-cart-remove]');
      (next || cartToggle)?.focus();
    } catch (error) {
      btn.removeAttribute('aria-busy');
      console.warn('[artink-header]', error);
    }
  };
  cartDrop?.addEventListener('click', onCartRemove);

  const onDocClick = (event) => {
    if (!root.contains(event.target)) {
      closeDrops();
      closeMega();
      closeCart();
      return;
    }
    if (cartWrap && !cartWrap.contains(event.target)) closeCart();
    if (!event.target.closest('[data-drop]')) closeDrops();
    if (!event.target.closest('[data-mega-wrap]') && !event.target.closest('[data-panel]')) {
      closeMega();
    }
  };

  const onKey = (event) => {
    if (event.key !== 'Escape') return;
    closeDrops();
    closeMega();
    closeCart();
    if (drawer && !drawer.hidden) {
      drawer.hidden = true;
      burger?.setAttribute('aria-expanded', 'false');
      bar.classList.remove('is-drawer');
      document.body.classList.remove('artink-nav-lock');
      burger?.focus();
    }
  };

  const toggleDrawer = () => {
    if (!drawer || !burger) return;
    const open = drawer.hidden;
    drawer.hidden = !open;
    burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    bar.classList.toggle('is-drawer', open);
    document.body.classList.toggle('artink-nav-lock', open);
    if (open) {
      closeDrops();
      closeMega();
      closeCart();
      // Measured once on open; the drawer fills the space below the bar and scrolls.
      const top = Math.max(0, Math.round(drawer.getBoundingClientRect().top));
      drawer.style.setProperty('--artink-drawer-top', `${top}px`);
      drawer.querySelector('a, button')?.focus({ preventScroll: true });
    }
  };

  // The drawer is hidden by CSS above 1100px; release the scroll lock if it was open.
  const desktopQuery = window.matchMedia('(min-width: 1101px)');
  const onDesktop = (event) => {
    if (!event.matches || !drawer || drawer.hidden) return;
    drawer.hidden = true;
    burger?.setAttribute('aria-expanded', 'false');
    bar.classList.remove('is-drawer');
    document.body.classList.remove('artink-nav-lock');
  };
  desktopQuery.addEventListener('change', onDesktop);

  burger?.addEventListener('click', toggleDrawer);
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onKey);

  function showAnnounce(index) {
    if (!announceItems.length) return;
    announceIndex = (index + announceItems.length) % announceItems.length;
    announceItems.forEach((item, i) => item.classList.toggle('is-on', i === announceIndex));
  }

  function startAnnounce() {
    window.clearInterval(announceTimer);
    if (announceItems.length < 2) return;
    announceTimer = window.setInterval(() => showAnnounce(announceIndex + 1), 4000);
  }

  root.querySelector('[data-announce-prev]')?.addEventListener('click', () => {
    showAnnounce(announceIndex - 1);
    startAnnounce();
  });
  root.querySelector('[data-announce-next]')?.addEventListener('click', () => {
    showAnnounce(announceIndex + 1);
    startAnnounce();
  });
  startAnnounce();

  const onCart = (event) => {
    const cart = event?.detail?.cart;
    if (cart && Array.isArray(cart.items)) {
      renderCartDropdown(cart);
      setBagCounts(cart.item_count ?? event?.detail?.itemCount ?? 0);
      return;
    }
    const qty =
      event?.detail?.cart?.totalQuantity ??
      event?.detail?.itemCount ??
      event?.detail?.cart?.item_count;
    if (typeof qty === 'number') setBagCounts(qty);
  };
  document.addEventListener('cart:update', onCart);
  document.addEventListener('cart:updated', onCart);

  // product-form.js and the cart page dispatch this before their request settles.
  const onLinesUpdate = async (event) => {
    try {
      await event.promise;
    } catch {
      return;
    }
    refreshCartCount().catch(() => {});
  };
  document.addEventListener('shopify:cart:lines-update', onLinesUpdate);

  patchOpenCart();

  const ro = new ResizeObserver(() => setHeaderHeights(root));
  ro.observe(root);
  setHeaderHeights(root);

  root._artinkNavCleanup = () => {
    window.clearInterval(announceTimer);
    window.clearTimeout(megaTimer);
    document.removeEventListener('click', onDocClick);
    document.removeEventListener('keydown', onKey);
    document.removeEventListener('cart:update', onCart);
    document.removeEventListener('cart:updated', onCart);
    document.removeEventListener('shopify:cart:lines-update', onLinesUpdate);
    desktopQuery.removeEventListener('change', onDesktop);
    burger?.removeEventListener('click', toggleDrawer);
    cartDrop?.removeEventListener('click', onCartRemove);
    ro.disconnect();
    document.body.classList.remove('artink-nav-lock');
    root.dataset.booted = '0';
  };
}

function initAll() {
  document.querySelectorAll('[data-artink-header]').forEach(bootHeader);
}

initAll();
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-artink-header]').forEach(bootHeader);
});
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-artink-header]').forEach((root) => {
    root._artinkNavCleanup?.();
  });
});
